import { create } from "zustand";
import axios from "axios";

// V2 API URL (일정관리 등 마이그레이션 완료된 기능)
const V2_API_URL = `${import.meta.env.VITE_API_V2_URL || 'http://localhost:8091/api/v1'}`;

// V2 API 인스턴스 - 일정관리 등 마이그레이션 완료된 기능용
const api = axios.create({
	baseURL: V2_API_URL,
});
api.interceptors.request.use(
	(config) => {
		const token = JSON.parse(localStorage.getItem("auth-storage"))?.state?.token;
		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
		}
		return config;
	},
	(error) => Promise.reject(error)
);

// 기존 backend-api URL (v2 미구현 기능용)
const LEGACY_API_URL = `${import.meta.env.VITE_SERVER_URL}`;

// Legacy API 인스턴스 - 로그 등 v2 미구현 기능용
const legacyApi = axios.create({
	baseURL: LEGACY_API_URL,
});
legacyApi.interceptors.request.use(
	(config) => {
		const token = JSON.parse(localStorage.getItem("auth-storage"))?.state?.token;
		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
		}
		return config;
	},
	(error) => Promise.reject(error)
);

export const useManagementStore = create((set, get) => ({
	isLoading: false,
	error: null,
	scheduleData: [],
	// 현재 확장된 티켓 ID (한 번에 하나만 열림)
	currentExpandedTicket: null,

	// 티켓 확장 상태 토글
	toggleTicketExpand: (ticketId) => {
		const currentExpanded = get().currentExpandedTicket;
		// 이미 확장된 티켓이면 닫기
		if (currentExpanded === ticketId) {
			set({ currentExpandedTicket: null });
		} else {
			// 다른 티켓이면 현재 티켓만 열기
			set({ currentExpandedTicket: ticketId });
		}
	},

	// 티켓 확장 상태 확인
	isTicketExpanded: (ticketId) => {
		return get().currentExpandedTicket === ticketId;
	},

	// 일정표 정보 조회 (기존 API - v2 미구현)
	getSchedules: async (startDate, endDate) => {
		set({ isLoading: true, error: null });
		try {
			const response = await legacyApi.get("/sms/getSms", {
				params: {
					start_date: startDate.replace(/-/g, ""),
					end_date: endDate.replace(/-/g, ""),
				},
			});

			const data = await response.data;

			set({
				smsLogs: data,
				isLoading: false,
			});

			return { success: true, data: data };
		} catch (err) {
			console.error("Error fetching SMS logs:", err);
			const errorMessage =
				err.message || "SMS 로그를 가져오는 중 오류가 발생했습니다.";
			set({
				isLoading: false,
				error: errorMessage,
			});
			return { success: false, error: errorMessage };
		}
	},

	// 일정표 정보 조회 (v2 API)
	getScheduleInfo: async (selectedYear, keyword = "") => {
		set({ isLoading: true, error: null });
		try {
			const response = await api.get("/schedules/info", {
				params: {
					selected_year: selectedYear,
					keyword: keyword,
				},
			});

			// v2 응답: { items: [...] }
			const data = response.data.items || response.data;
			set({
				scheduleData: data,
				isLoading: false,
			});

			return { success: true, data: data };
		} catch (err) {
			console.error("일정표 정보 조회 중 오류 발생:", err);
			const errorMessage =
				err.response?.data?.detail || err.message || "일정표 정보를 가져오는 중 오류가 발생했습니다.";
			set({
				isLoading: false,
				error: errorMessage,
			});
			return { success: false, error: errorMessage };
		}
	},

	// 일정 이미지 등록 API (v2 API)
	uploadScheduleImage: async (file, eventData) => {
		try {
			const formData = new FormData();
			formData.append("file", file);
			formData.append(
				"GOOD_TYPE_CD",
				eventData.GOOD_TYPE_CD.toString() || ""
			);
			formData.append("AREA_CD", eventData.AREA_CD.toString() || "");
			formData.append("GOOD_YY", eventData.GOOD_YY.toString() || "");
			formData.append("GOOD_SEQ", eventData.GOOD_SEQ.toString() || "");

			const response = await api.post(
				"/schedules/upload-image",
				formData,
				{
					headers: {
						"Content-Type": "multipart/form-data",
					},
				}
			);

			return { success: true, data: response.data };
		} catch (error) {
			console.error("일정 이미지 업로드 오류:", error);
			const errorMessage =
				error.response?.data?.detail ||
				"일정 이미지 업로드 중 오류가 발생했습니다.";
			return { success: false, error: errorMessage };
		}
	},

	// 일정 등록 API (v2 API)
	registerSchedule: async (scheduleData) => {
		try {
			const response = await api.post("/schedules", scheduleData);
			return { success: true, data: response.data };
		} catch (error) {
			console.error("일정 등록 오류:", error);
			const errorMessage =
				error.response?.data?.detail ||
				"일정 등록 중 오류가 발생했습니다.";
			return { success: false, error: errorMessage };
		}
	},

	// 일정 삭제 API (v2 API)
	deleteSchedule: async (scheduleId) => {
		try {
			const data = {
				schedule_id: scheduleId,
			};
			const response = await api.delete("/schedules", { data });
			return { success: true, data: response.data };
		} catch (error) {
			console.error("일정 삭제 오류:", error);
			const errorMessage =
				error.response?.data?.detail ||
				"일정 삭제 중 오류가 발생했습니다.";
			return { success: false, error: errorMessage };
		}
	},

	// 일정 수정 API (v2 API)
	updateSchedule: async (scheduleId, scheduleData, file) => {
		try {
			// FormData 생성
			const formData = new FormData();

			// 새로운 데이터 객체 생성
			let newData = {};

			// 파일이 있는 경우와 없는 경우 처리
			if (file) {
				newData = {
					schedule_id: scheduleId,
					old_image_url:
						scheduleData.s3PublicUrl ||
						scheduleData.old_image_url ||
						"-",
					name_string:
						scheduleData.nameString || scheduleData.name_string,
					guide_name_string:
						scheduleData.guideNameString ||
						scheduleData.guide_name_string,
					guide_phone_string:
						scheduleData.guidePhoneString ||
						scheduleData.guide_phone_string,
					meeting_place_string:
						scheduleData.meetingPlaceString ||
						scheduleData.meeting_place_string,
				};

				// 업데이트된 이미지 추가
				formData.append("updated_image", file);
			} else {
				newData = {
					schedule_id: scheduleId,
					old_image_url: "-",
					name_string:
						scheduleData.nameString || scheduleData.name_string,
					guide_name_string:
						scheduleData.guideNameString ||
						scheduleData.guide_name_string,
					guide_phone_string:
						scheduleData.guidePhoneString ||
						scheduleData.guide_phone_string,
					meeting_place_string:
						scheduleData.meetingPlaceString ||
						scheduleData.meeting_place_string,
				};
			}

			// 데이터를 JSON 문자열로 변환하여 추가
			formData.append("data", JSON.stringify(newData));

			// API 호출
			const response = await api.put("/schedules", formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});

			return { success: true, data: response.data };
		} catch (error) {
			console.error("일정 수정 오류:", error);
			const errorMessage =
				error.response?.data?.detail ||
				"일정 수정 중 오류가 발생했습니다.";
			return { success: false, error: errorMessage };
		}
	},

	// 전화상담관리 현황 조회 API (v2 API)
	getCallList: async (startDate, endDate) => {
		set({ isLoading: true, error: null });

		try {
			const response = await api.get("/call-management", {
				params: {
					start_day: startDate,
					end_day: endDate,
				},
			});

			// v2 응답: { items: [...], total: number }
			const data = response.data.items || response.data;
			set({ isLoading: false });
			return { success: true, data };
		} catch (error) {
			console.error("전화상담 현황 조회 중 오류 발생:", error);
			const errorMessage =
				error.response?.data?.detail ||
				error.message ||
				"전화상담 현황을 가져오는 중 오류가 발생했습니다.";
			set({ isLoading: false, error: errorMessage });
			return { success: false, error: errorMessage };
		}
	},

	// 상담완료 API (v2 API)
	handleFinishCall: async (parseData) => {
		try {
			const response = await api.put("/call-management/status", {
				user_id: parseData.user_id,
				RES_NM: parseData.RES_NM,
				call_finished_flag: true,
			});

			const data = response.data;
			return { success: true, data };
		} catch (error) {
			console.error("상담완료 처리 중 오류 발생:", error);
			const errorMessage =
				error.response?.data?.detail ||
				error.message ||
				"상담완료 처리 중 오류가 발생했습니다.";
			return { success: false, error: errorMessage };
		}
	},

	// 문자재발송 API (v2 API)
	handleReMessage: async (parseData) => {
		try {
			const response = await api.post("/call-management/resend", {
				user_id: parseData.user_id,
			});

			const data = response.data;
			return { success: true, data };
		} catch (error) {
			console.error("문자 재발송 중 오류 발생:", error);
			const errorMessage =
				error.response?.data?.detail ||
				error.message ||
				"문자 재발송 중 오류가 발생했습니다.";
			return { success: false, error: errorMessage };
		}
	},

	// 감사 로그 조회 API (v2)
	getAuditLogs: async (params = {}) => {
		set({ isLoading: true, error: null });
		try {
			const response = await api.get("/admin/audit-logs", { params });
			set({ isLoading: false });
			return { success: true, data: response.data.items || [], total: response.data.total || 0 };
		} catch (err) {
			console.error("감사 로그 조회 중 오류 발생:", err);
			set({ isLoading: false });
			const errorMessage =
				err.response?.data?.message ||
				err.message ||
				"감사 로그를 가져오는 중 오류가 발생했습니다.";
			return { success: false, error: errorMessage };
		}
	},
}));
